import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { motion, useReducedMotion } from 'framer-motion';
import { X, Check, CalendarDays } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { App as NativeApp } from '@capacitor/app';
import { useData } from '../contexts/DataContext';
import { PAY_MODELS, describePayStructure } from '../features/payperiod/payStructure';
import { isoDate } from '../features/payperiod/periods';
import { grossForEntry, money, pennies } from '../features/home/homeModel';
import { draftFromLog, sameWork, validateDraft } from '../features/home/quickEntryModel';
import { trackProductEvent as track } from '../services/productAnalytics';
import '../styles/home.css';
import '../styles/quick-entry.css';

export default function DailyQuickEntry({ open, initialDate, onDateChange, onClose, onSaved }) {
  const [date, setDate] = useState(initialDate || isoDate(new Date()));
  const [transfer, setTransfer] = useState(null);
  const echoedDate = useRef(null);
  useEffect(() => {
    if (!open) return;
    // Home mirrors the date into initialDate. Don't erase the draft on that echo.
    if (echoedDate.current === initialDate) { echoedDate.current = null; return; }
    echoedDate.current = null;
    setDate(initialDate || isoDate(new Date()));
    setTransfer(null);
  }, [open, initialDate]);
  if (!open) return null;
  function changeDate(value, carriedDraft) {
    setTransfer(carriedDraft ? { date:value, ...carriedDraft } : null);
    setDate(value);
    if (onDateChange) {
      echoedDate.current = value;
      onDateChange(value);
    }
  }
  return <EntryEditor key={date} date={date} transfer={transfer?.date===date?transfer:null}
    onDateChange={changeDate} onClose={onClose} onSaved={onSaved} />;
}

function EntryEditor({ date, transfer, onDateChange, onClose, onSaved }) {
  const { logs = [], paymentConfig, updateLogs, loading, loadError, hasSavedPayStructure, payStructureVersions = [], payHistoryLoading, payHistoryError } = useData();
  const rows = logs.filter(row => row.date === date);
  const [chosen,setChosen]=useState(null);
  const pastNew=!rows.length&&date<isoDate(new Date());
  // Wait for the actual record before initializing the form.
  if (loading || loadError) return <Dialog open onClose={onClose} className="qe-root"><div className="qe-backdrop" /><div className="qe-position"><Dialog.Panel className="home-screen qe-panel"><Dialog.Title>Quick Entry</Dialog.Title><p role={loadError ? 'alert' : 'status'}>{loadError || 'Loading your work…'}</p><button className="home-secondary" onClick={onClose}>Close</button></Dialog.Panel></div></Dialog>;
  if (!rows.length && hasSavedPayStructure===false) return <Dialog open onClose={onClose} className="qe-root"><div className="qe-backdrop"/><div className="qe-position"><Dialog.Panel className="home-screen qe-panel qe-discard"><Dialog.Title>Set up your pay first</Dialog.Title><p>Choose how you get paid before calculating a new entry.</p><a className="home-primary" href="/app/settings">Set up pay structure</a><button className="home-secondary" onClick={onClose}>Close</button></Dialog.Panel></div></Dialog>;
  if(pastNew && hasSavedPayStructure===true && !chosen) return <HistoricalRateChoice versions={payStructureVersions} current={paymentConfig} loading={payHistoryLoading} error={payHistoryError} onChoose={setChosen} onClose={onClose}/>;
  return <ReadyEditor date={date} rows={rows} logs={logs} paymentConfig={chosen||paymentConfig} transfer={transfer} updateLogs={updateLogs} onDateChange={onDateChange} onClose={onClose} onSaved={onSaved} />;
}

function HistoricalRateChoice({versions,current,loading,error,onChoose,onClose}){
 const [id,setId]=useState('');
 const choices=[{id:'current',config:current,label:'Current arrangement'},...versions.filter(v=>v.config&&v.id!==current?.versionId).map(v=>({...v,label:v.savedAt?`Saved ${new Date(v.savedAt).toLocaleDateString('en-GB')}`:'Previous arrangement · date unknown'}))];
 return <Dialog open onClose={onClose} className="qe-root"><div className="qe-backdrop"/><div className="qe-position"><Dialog.Panel className="home-screen qe-panel qe-discard"><Dialog.Title>Which rates applied that day?</Dialog.Title><p>This is a new entry for a past day. Choose the applicable arrangement before logging work.</p>{loading?<p role="status">Loading saved rates…</p>:error?<p role="alert">{error} Close and reopen to retry.</p>:<><label className="home-field">Pay structure<select value={id} onChange={e=>setId(e.target.value)}><option value="">Choose rates</option>{choices.map(v=><option key={v.id} value={v.id}>{v.label} · {describePayStructure(v.config)}</option>)}</select></label><button className="home-primary" disabled={!id} onClick={()=>onChoose(choices.find(v=>v.id===id).config)}>Use these rates</button></>}<button className="home-secondary" onClick={onClose}>Cancel</button></Dialog.Panel></div></Dialog>;
}

function ReadyEditor({ date, rows, logs, paymentConfig, transfer, updateLogs, onDateChange, onClose, onSaved }) {
  const original = useRef(rows[0]);
  const config = useRef(original.current?.payStructureSnapshot || paymentConfig).current;
  const meta = PAY_MODELS.find(model => model.id === config?.model);
  const baseline = useRef(draftFromLog(original.current, config?.model)).current;
  const [draft, setDraft] = useState(() => {
    // Only carry fields into another EMPTY workday. Never replace a saved record's data.
    if (original.current || !transfer?.draft) return baseline;
    const fields = { ...transfer.draft };
    // A historic pay structure can describe a different unit of work (stops vs
    // hours/days/miles). Keep optional extras, but never silently reinterpret quantity.
    if (transfer.model && transfer.model !== config?.model) {
      fields.quantity = '';
      fields.parcels = '';
      fields.miles = '';
    }
    return fields;
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(null);
  const [showDate, setShowDate] = useState(false);
  const [viewport, setViewport] = useState(null);
  const saveLock = useRef(false);
  const id = useRef(original.current?.id || crypto.randomUUID());
  const titleRef = useRef(null);
  const formRef = useRef(null);
  const scrollRef = useRef(null);
  const closeHandler = useRef(null);
  const reducedMotion = useReducedMotion();
  const today = isoDate(new Date());
  const dateLabel = date===today?'Today · '+new Date(date+'T12:00:00').toLocaleDateString('en-GB',{day:'numeric',month:'short'})
    :new Date(date+'T12:00:00').toLocaleDateString('en-GB',{weekday:'short',day:'numeric',month:'short',year:'numeric'});
  const editing = !!original.current;
  const dirty = JSON.stringify(draft) !== JSON.stringify(baseline);
  const parcelsRelevant = ['flat_stops', 'tiered_stops', 'sliding_scale'].includes(config?.model) && Number(config?.excessParcelRate) > 0;
  const validity = validateDraft(draft, config?.model, parcelsRelevant, date, today);
  const workUnchanged = editing && sameWork(draft, baseline);
  const legacy = editing && !original.current.payStructureSnapshot;
  const calculated = meta && !Object.keys(validity).length ? grossForEntry(config, { quantity: Number(draft.quantity), miles: Number(draft.miles || 0), totalParcels: parcelsRelevant ? draft.parcels : '', extra: draft.extraWork ? Number(draft.amount || 0) : 0 }) : null;
  const gross = legacy && workUnchanged ? original.current.total : calculated;
  const grossKnown = gross != null && Number.isFinite(Number(gross)) && Number.isSafeInteger(pennies(gross));
  const rate = legacy && workUnchanged ? null : config?.contractorFeePercent;
  const feeKnown = rate != null && Number.isFinite(Number(rate)) && Number(rate) >= 0 && Number(rate) <= 100;
  const fee = feeKnown && grossKnown ? Math.round(pennies(gross) * Number(rate) / 100) / 100 : null;
  const transition = { duration: reducedMotion ? 0 : 0.2 };
  const question = { per_day: 'Did you work today?', hourly: 'How many hours did you work?', per_mile: 'How many miles did you drive?' }[config?.model] || 'How many stops did you do today?';
  const primaryLabel = { per_day: 'Worked today', hourly: 'Hours worked', per_mile: 'Miles driven' }[config?.model] || 'Stops';

  // Android's keyboard shrinks the visual viewport, not necessarily window.innerHeight.
  // Keep the sheet inside that viewport and scroll the *fields* rather than the locked body.
  useEffect(() => {
    const view = window.visualViewport;
    const update = () => setViewport({
      height: Math.max(240, view?.height || window.innerHeight),
      top: view?.offsetTop || 0,
    });
    update();
    view?.addEventListener('resize', update);
    view?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      view?.removeEventListener('resize', update);
      view?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  function revealField(target) {
    const region = scrollRef.current;
    if (!region || !target || !region.contains(target)) return;
    const align = () => {
      if (!scrollRef.current || !scrollRef.current.contains(target)) return;
      const visible = region.getBoundingClientRect();
      const field = target.getBoundingClientRect();
      const margin = 18;
      if (field.bottom > visible.bottom - margin) region.scrollTop += field.bottom - visible.bottom + margin;
      else if (field.top < visible.top + margin) region.scrollTop -= visible.top - field.top + margin;
    };
    requestAnimationFrame(align);
    window.setTimeout(align, 140);
    window.setTimeout(align, 350);
  }
  useEffect(() => {
    const active = document.activeElement;
    if (active?.matches?.('input, textarea, select')) revealField(active);
  }, [viewport?.height, viewport?.top, draft.extraWork]);
  useEffect(() => {
    if (!dirty) return;
    const prevent = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', prevent);
    return () => window.removeEventListener('beforeunload', prevent);
  }, [dirty]);
  closeHandler.current = () => {
    if (saveLock.current) return;
    if (pending) { setPending(null); return; }
    if (dirty) setPending({ kind: 'close' });
    else { track('quick_entry_dismissed', { mode: editing ? 'edit' : 'new' }); onClose(); }
  };
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let disposed = false;
    let listener;
    NativeApp.addListener('backButton', () => {
      const active = document.activeElement;
      if (window.visualViewport && window.innerHeight - window.visualViewport.height > 150 && active?.matches('input, textarea')) active.blur();
      else closeHandler.current?.();
    }).then(handle => { if (disposed) handle.remove(); else listener = handle; });
    return () => { disposed = true; listener?.remove(); };
  }, []);

  function change(field, value) {
    setDraft(previous => ({ ...previous, [field]: value }));
    setErrors(previous => ({ ...previous, [field]: undefined, save: undefined }));
  }
  function changeDate(value) {
    if (!value || value === date) return;
    const destination = logs.filter(row => row.date === value);
    if (destination.length) {
      // A saved day must always open its own record, never inherit unsaved fields.
      setPending({ kind:'saved-date', value });
    } else if (editing && dirty) {
      // Moving away from an edited existing record can discard genuine changes.
      setPending({ kind:'discard-edit', value });
    } else if (editing) {
      onDateChange(value, null);
    } else {
      // Correcting the date on a new entry must not reset its work fields.
      onDateChange(value, { draft, model:config?.model });
    }
  }
  function discard() {
    if (pending.kind === 'saved-date' || pending.kind === 'discard-edit') onDateChange(pending.value, null);
    else { track('quick_entry_dismissed', { mode: editing ? 'edit' : 'new' }); onClose(); }
    setPending(null);
  }
  async function save(event) {
    event.preventDefault();
    if (saveLock.current) return;
    const nextErrors = { ...validity };
    if (!meta || !grossKnown) nextErrors.save = 'Earnings could not be calculated. Check your pay structure.';
    if (rows.length > 1) nextErrors.save = 'Multiple records exist for this date. Review them in View records before editing.';
    if (JSON.stringify(rows[0]) !== JSON.stringify(original.current)) nextErrors.save = 'This record changed while you were editing. Close and reopen it before saving.';
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      requestAnimationFrame(() => {
        const target = formRef.current?.querySelector('[aria-invalid="true"], [role="alert"]');
        target?.scrollIntoView?.({ block: 'center', behavior: reducedMotion ? 'auto' : 'smooth' });
        target?.focus?.();
      });
      track('quick_entry_validation_failed', { mode: editing ? 'edit' : 'new' });
      return;
    }
    saveLock.current = true; setSaving(true); setErrors({});
    try {
      const quantity = Number(draft.quantity);
      const record = { ...original.current, id: id.current, date, quantity,
        stops: meta.primary.field === 'stops' ? quantity : (original.current?.stops || 0),
        miles: meta.primary.field === 'miles' ? quantity : Number(draft.miles || 0),
        extraWork: draft.extraWork, extra: draft.extraWork ? Number(draft.amount || 0) : 0,
        extraType: draft.extraWork ? draft.extraType || null : null, notes: draft.notes.trim(),
        total: Number(gross), payModel: config.model, timestamp: new Date().toISOString() };
      if (parcelsRelevant && draft.parcels !== '') record.totalParcels = Number(draft.parcels);
      else delete record.totalParcels;
      if (meta.primary.field === 'hours') record.hours = quantity;
      if (!legacy || !workUnchanged) record.payStructureSnapshot = JSON.parse(JSON.stringify(config));
      const result = await updateLogs([record, ...logs.filter(row => row.date !== date)].sort((a, b) => b.date.localeCompare(a.date)));
      if (result?.success === false) throw new Error('Save rejected');
      track('daily_entry_saved', { mode: editing ? 'edit' : 'new', source: 'home' });
      onSaved?.(record, result);
    } catch (_) { setErrors({ save: "Couldn't save. Your entry is still here — please try again." }); }
    finally { saveLock.current = false; setSaving(false); }
  }
  const error = field => errors[field] ? <p id={'qe-error-' + field} role="alert" className="home-error">{errors[field]}</p> : null;
  const inputProps = field => ({ value: draft[field], onChange: event => change(field, event.target.value), 'aria-invalid': !!errors[field], 'aria-describedby': errors[field] ? 'qe-error-' + field : undefined });
  return <Dialog open onClose={() => closeHandler.current?.()} initialFocus={titleRef} className="qe-root">
    <div className="qe-backdrop" aria-hidden="true" />
    <div className="qe-position" style={viewport ? { height: viewport.height, top: viewport.top } : undefined}>
      <Dialog.Panel as={motion.div} initial={reducedMotion ? false : { opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={transition} className="home-screen qe-panel">
        <header className="qe-header"><div><Dialog.Title ref={titleRef} tabIndex={-1} className="qe-title">{editing ? 'Edit workday' : 'Log your day'}</Dialog.Title></div><button type="button" className="home-icon" aria-label="Close Quick Entry" disabled={saving} onClick={() => closeHandler.current?.()}><X /></button></header>
        {pending ? <div className="qe-discard" role="alert">
          <h3>{pending.kind==='saved-date'?'This date already has work saved':pending.kind==='discard-edit'?'Discard edits to this workday?':'Discard unsaved changes?'}</h3>
          <p>{pending.kind==='saved-date'?'Open the saved workday instead? Your current unsaved entry will not be copied over it.':pending.kind==='discard-edit'?'Switching dates will discard the changes to this saved workday.':'Your changes have not been saved.'}</p>
          <button type="button" className="home-primary w-full" onClick={() => setPending(null)}>Keep editing</button>
          <button type="button" className="home-secondary w-full" onClick={discard}>{pending.kind==='saved-date'?'Open saved workday':pending.kind==='discard-edit'?'Discard edits and change date':'Discard changes'}</button>
        </div> :
        <form ref={formRef} onSubmit={save} noValidate className="qe-form">
          <fieldset ref={scrollRef} disabled={saving} className="qe-scroll" onFocusCapture={event => revealField(event.target)}>
            <div className="qe-date-compact">
              <button type="button" className="qe-date-trigger" aria-expanded={showDate} aria-controls="qe-date-selector" onClick={()=>setShowDate(value=>!value)}>
                <span><CalendarDays size={18} aria-hidden="true"/>{dateLabel}</span>
                <span className="qe-date-change">{showDate?'Close':'Change'}</span>
              </button>
              {showDate&&<label id="qe-date-selector" className="home-field qe-date-input">Entry date
                <input aria-label="Entry date" type="date" max={today} value={date} onChange={event=>changeDate(event.target.value)}/>
              </label>}
              {error('date')}
            </div>
            <h3 className="qe-question">{config?.model === "per_day" ? question : config?.model === "hourly" ? "Hours worked" : config?.model === "per_mile" ? "Miles driven" : "Your deliveries"}</h3>
            {meta?.primary.field === 'day' ? <div className="qe-worked" role="group" aria-label="Worked today"><button type="button" aria-pressed={draft.quantity === '1'} onClick={() => change('quantity', '1')}>Yes, I worked</button><button type="button" aria-pressed={draft.quantity === '0'} onClick={() => change('quantity', '0')}>No work today</button></div> :
            <div className={parcelsRelevant ? 'qe-counts' : 'qe-counts qe-single'}><label className="home-field">{primaryLabel}<input {...inputProps('quantity')} inputMode={meta?.primary.field === 'stops' ? 'numeric' : 'decimal'} placeholder="0" autoComplete="off" /></label>{parcelsRelevant && <label className="home-field">Total parcels<input {...inputProps('parcels')} inputMode="numeric" placeholder="Optional" autoComplete="off" /></label>}</div>}
            {error('quantity')}{error('parcels')}
            {parcelsRelevant && <p className={draft.parcels !== '' && validity.parcels ? "qe-hint qe-inline-error" : "home-muted qe-hint"}>{draft.parcels === '' ? 'Parcels optional · add them later if needed.' : validity.parcels ? 'Total parcels must be at least the number of stops.' : validity.quantity ? 'Enter your stops to calculate excess parcels.' : Number(draft.parcels) - Number(draft.quantity) + ' excess parcels'}</p>}
            {config?.model === 'sliding_scale' && <label className="home-field">Miles driven<input {...inputProps('miles')} inputMode="decimal" />{error('miles')}</label>}
            <section className="qe-extra">
              <button type="button" className="qe-extra-toggle" role="switch" aria-checked={draft.extraWork} aria-controls="qe-extras" onClick={() => change('extraWork', !draft.extraWork)}>
                <span>Extra work <small>{draft.extraWork ? 'Add run details below' : 'Only if you did extra work'}</small></span>
                <span className={'qe-switch ' + (draft.extraWork ? 'qe-switch-on' : '')} aria-hidden="true"><span /></span>
              </button>
              {draft.extraWork && <motion.div id="qe-extras" initial={reducedMotion ? false : { opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} transition={transition} className="qe-extra-fields">
                <label className="home-field">Extra payment (£)<input {...inputProps('amount')} inputMode="decimal" placeholder="e.g. 30.00" autoComplete="off"/></label>
                {error('amount')}
                <details className="qe-extra-more" defaultOpen={!!draft.extraType}>
                  <summary>Work type (optional){draft.extraType?' · '+draft.extraType:''}</summary>
                  <label className="home-field">Type of extra work
                    <select {...inputProps('extraType')}><option value="">Not specified</option>{Array.from(new Set(['Extra run','Collection','Return','Extra route','Other',draft.extraType].filter(Boolean))).map(type=><option key={type}>{type}</option>)}</select>
                  </label>
                </details>
              </motion.div>}
            </section>
            <details className="qe-optional-note" defaultOpen={!!draft.notes}>
              <summary>Add a note (optional){draft.notes ? ' · added' : ''}</summary>
              <label className="home-field">Work notes<textarea {...inputProps('notes')} rows={2} maxLength={1000} placeholder="Anything to remember about this day?"/></label>
            </details>
            <details className="qe-breakdown"><summary>View pay breakdown</summary>
              <dl>
                <div><dt>Gross earnings</dt><dd>{grossKnown && draft.quantity !== '' && !Object.keys(validity).length ? money(gross) : '—'}</dd></div>
                <div><dt>Contractor fee{feeKnown ? ' · ' + rate + '%' : ''}</dt><dd>{fee != null && !Object.keys(validity).length ? money(-fee) : 'Not available'}</dd></div>
                <div><dt>After contractor fee</dt><dd>{fee != null && !Object.keys(validity).length ? money((pennies(gross) - pennies(fee)) / 100) : '—'}</dd></div>
              </dl>
              <p className="home-muted qe-hint">Before personal expenses and tax.</p>
            </details>
            {legacy && <p className="qe-hint home-banner">This older entry has no saved rates. Its original gross stays unchanged unless you change work quantities or extra payment; those changes use your current pay structure.</p>}
            {rows.length > 1 && <p role="alert" className="home-error">Multiple records exist for this day. Review them in View records before saving.</p>}
            {error('save')}
          </fieldset>
          <footer className="qe-footer">
            <div className="qe-footer-estimate" aria-label="Live earnings estimate">
              <span>{feeKnown ? 'Estimated after contractor fee' : 'Estimated gross'}</span>
              <strong aria-live="polite">{draft.quantity !== '' && !Object.keys(validity).length && grossKnown ? fee != null ? money((pennies(gross) - pennies(fee)) / 100) : money(gross) : '—'}</strong>
            </div>
            <button type="submit" className="home-primary w-full" disabled={saving || rows.length > 1 || !meta}><Check size={20} aria-hidden="true" />{saving ? 'Saving…' : editing ? 'Save changes' : 'Save day'}</button>
          </footer>
        </form>}
      </Dialog.Panel>
    </div>
  </Dialog>;
}
